import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useApoAuth } from "..";
import {
  selectedTransferReset,
  transferFiltersReset,
  transferSearchFormReset,
  transferSearchFormUpdate,
  transferSearchResultsFailed,
  transferSearchResultsIdle,
  transferSearchResultsLoading,
  transferSearchResultsReset,
  transferSearchResultsUpdate,
} from "../../actions/search/transfer_search";
import { useCallback, useEffect, useState } from "react";
import { changeServiceSessionIds } from "../../actions/search/session_ids";
import _ from "lodash";
import {
  pollTransfersSearch,
  searchRequestTransfersPOST,
} from "../../api/transfers";
import { toast } from "react-toastify";
import { transfersErrorMsgs } from "../../errors";
import {
  retrieveSearchSessionMetadata,
  storeSearchSessionMetadata,
} from "../../api";

export function useTransferActions() {
  const dispatch = useDispatch();

  const onFormUpdate = useCallback(
    (form) => dispatch(transferSearchFormUpdate({ form })),
    [dispatch]
  );

  const onChangeSessionIDs = useCallback(
    (value) => dispatch(changeServiceSessionIds("TRF", value)),
    [dispatch]
  );

  const onSearchResultsLoading = useCallback(
    () => dispatch(transferSearchResultsLoading()),
    [dispatch]
  );

  const onFailed = useCallback(() => {
    dispatch(transferSearchResultsIdle());
    dispatch(transferSearchResultsFailed());
  }, [dispatch]);

  const onClearState = useCallback(() => {
    dispatch(transferSearchFormReset());
    dispatch(transferSearchResultsReset());
    dispatch(transferFiltersReset());
    dispatch(selectedTransferReset());
  }, [dispatch]);

  const onSearchResultsUpdate = useCallback(
    (data) => dispatch(transferSearchResultsUpdate(data)),
    [dispatch]
  );

  return {
    onFormUpdate,
    onChangeSessionIDs,
    onSearchResultsLoading,
    onFailed,
    onClearState,
    onSearchResultsUpdate,
  };
}

function actionFailed({ history, onFailed, errorMsg }) {
  onFailed();
  history.push("/");
  toast.warning(errorMsg, {
    duration: 3000,
  });
}

function getTransferSearchPayload({
  form,
  market_type,
  source_entity,
  website_api_key,
}) {
  var output = {};
  if (form.tripMode === "air_to_poi") {
    output = {
      origin_lat: form.origin.latitude,
      origin_lng: form.origin.longitude,
      origin_name: form.origin.value,
      origin_country_code: form.origin.country_code,

      destination_lat: form.destination.computed_data.geodata.lat,
      destination_lng: form.destination.computed_data.geodata.lng,
      destination_name: form.destination.value,
      destination_country_code: form.destination.country_code,
    };
  } else {
    output = {
      origin_lat: form.origin.computed_data.geodata.lat,
      origin_lng: form.origin.computed_data.geodata.lng,
      origin_name: form.origin.value,
      origin_country_code: form.origin.country_code,

      destination_lat: form.destination.latitude,
      destination_lng: form.destination.longitude,
      destination_name: form.destination.value,
      destination_country_code: form.destination.country_code,
    };
  }

  output = {
    ...output,
    inbound: form.inboundPickupDate,
    adults: form.adults,
    children: form.children,
    page_size: form.page_size || 1,
    origin_iata: form.origin.iata,
    destination_iata: form.destination.iata ? form.destination.iata : null,
    source_entity,
    market_type,
    website_api_key,
    init_search: form.init_search || false,
  };

  return output;
}
export function useTransfersInitSearch({
  setLoadingSearchBtn = () => {},
} = {}) {
  const { onFormUpdate, onChangeSessionIDs, onSearchResultsLoading, onFailed } =
    useTransferActions();

  const history = useHistory();

  const search_form = useSelector((state) => state.TransfersSearchForm);

  const { source_entity, market_type, website_api_key } = useApoAuth();

  async function initSearch(form) {
    if (_.isEmpty(form)) {
      return;
    }

    onSearchResultsLoading();

    const fullForm = { ...search_form, ...form };

    const payload = getTransferSearchPayload({
      form: fullForm,
      market_type,
      source_entity,
      website_api_key,
    });

    onFormUpdate({ ...fullForm, searchPayload: payload });

    const response = await searchRequestTransfersPOST({
      payload,
      onError: () => {
        actionFailed({
          history,
          onFailed,
          errorMsg: transfersErrorMsgs.general.no_session,
        });
        setLoadingSearchBtn(false);
      },
    });

    if (response && !response.session_id) {
      actionFailed({
        history,
        onFailed,
        errorMsg: transfersErrorMsgs.general.no_session,
      });
      setLoadingSearchBtn(false);
      return;
    }

    const search_session_id = _.get(response, "session_id", "");

    const searchSessionMetaResult = await storeSearchSessionMetadata({
      service_type: "TRF",
      session_data: {
        search_session_id,
        suppliers_used: response.suppliers_used,
        search_form: { ...fullForm, searchPayload: payload },
      },
    });

    if (
      _.isEmpty(_.get(searchSessionMetaResult, "session_data", {})) ||
      !searchSessionMetaResult.session_id
    ) {
      actionFailed({
        history,
        onFailed,
        errorMsg: transfersErrorMsgs.general.no_session,
      });
      setLoadingSearchBtn(false);
      return;
    }
    const { session_id: page_session_id } = searchSessionMetaResult;

    onChangeSessionIDs({ page_session_id, search_session_id });

    history.push(`/transfers/${page_session_id}`);
  }
  return [initSearch];
}

export function useTransfersGetSessionMeta({ page_session_id }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { onFormUpdate, onChangeSessionIDs, onSearchResultsLoading, onFailed } =
    useTransferActions();

  const { allTransfersLength, search_session_id } = useSelector((state) => {
    return {
      allTransfersLength: _.get(
        state.TransfersSearchResults,
        "transfers.length",
        0
      ),
      loading: _.get(state.TransfersSearchResults, "loading", false),
      search_session_id: state.SessionIds.TRF.search_session_id,
    };
  });

  const [loadingMeta, setLoadingMeta] = useState(
    search_session_id ? false : true
  );

  const pageSessionFetcher = useCallback(
    ({ page_session_id }) => {
      onSearchResultsLoading();
      retrieveSearchSessionMetadata({ page_session_id })
        .then(async (data) => {
          if (!data) {
            actionFailed({
              history,
              onFailed,
              errorMsg: transfersErrorMsgs.searchForm.no_session,
            });
            return;
          }

          const form = _.get(data, "session_data.data.search_form", {});

          onChangeSessionIDs({
            page_session_id,
            search_session_id: _.get(
              data,
              "session_data.data.search_session_id",
              ""
            ),
          });

          await onFormUpdate(form);
          setLoadingMeta(false);
        })
        .catch(() => {
          actionFailed({
            history,
            onFailed,
            errorMsg: transfersErrorMsgs.general.no_session,
          });
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (allTransfersLength) {
      return;
    }
    if (!page_session_id) {
      actionFailed({
        history,
        onFailed,
        errorMsg: transfersErrorMsgs.general.no_session,
      });
      return;
    }
    if (!search_session_id) {
      setLoadingMeta(true);
      pageSessionFetcher({ page_session_id });
      return;
    }
  }, [search_session_id, page_session_id]);

  return { search_session_id, loadingMeta };
}

export function useTransfersPoller({ search_session_id }) {
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const { onSearchResultsLoading, onFailed, onSearchResultsUpdate } =
    useTransferActions();

  const maxPollCount = 7;

  function rePoller({ pollCount }) {
    setTimeout(() => poller({ pollCount }), 3000);
  }

  async function poller({ pollCount = 0 }) {
    const poll_response = await pollTransfersSearch({
      session_id: search_session_id,
      onError: (error) => {
        if (error.response?.status === 400) {
          return { results: [], status: "working", error: { status: null } };
        }
        return {
          error: {
            status: error.response?.status,
            message: error.response?.data.message,
          },
        };
      },
    });

    if (
      poll_response.error?.status ||
      (pollCount >= maxPollCount && poll_response.results.length === 0)
    ) {
      actionFailed({
        history,
        onFailed,
        errorMsg: poll_response.error?.status
          ? poll_response.error.message
          : transfersErrorMsgs.resultsList.no_results,
      });
      return;
    }
    const is_done = _.get(poll_response, "status", "") === "done";
    const results = _.get(poll_response, "results", []);
    const has_results = results.length > 0;
    if (is_done && !has_results) {
      actionFailed({
        history,
        onFailed,
        errorMsg: transfersErrorMsgs.resultsList.no_results,
      });
      return;
    }
    if (has_results) {
      onSearchResultsUpdate({ transfers: results, loading: false });
    }
    if (!is_done && pollCount < maxPollCount) {
      rePoller({ pollCount: pollCount + 1 });
    }
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    if (!search_session_id || !loading) {
      return;
    }
    onSearchResultsLoading();

    poller({ pollCount: 0 });
  }, [search_session_id, loading]);
}
